// Pool de workers especializados.
// Mantém um worker por role; despacha tool-runs; faz heartbeat; broadcast
// snapshots via BroadcastChannel("wuwei-agent-pool").
//
// Roda no main thread. Workers são instanciados via Vite ?worker (módulos
// ESM, importação estática que o build resolve).

import ToolWorker from "../workers/tool-worker.ts?worker";
import ExtractorWorker from "../workers/extractor-worker.ts?worker";
import { detectDevice, type DeviceCapabilities } from "./device-tier.ts";
import { TOOL_ROLES } from "../tools/_registry.ts";
import { publish as relayPublish } from "./relay-client.ts";
import type { AgentSupervisor, WorkerRole } from "./types.ts";

/** Sanitiza um valor pra serialização em log (trunca strings/arrays grandes). */
function sanitize(value: unknown, maxStringLen = 512): unknown {
  if (value == null) return value;
  if (typeof value === "string") {
    if (value.length > maxStringLen) {
      return { __truncated: true, length: value.length, preview: value.slice(0, maxStringLen) };
    }
    return value;
  }
  if (typeof value !== "object") return value;
  if (value instanceof ArrayBuffer) {
    return { __buffer: true, byteLength: value.byteLength };
  }
  if (ArrayBuffer.isView(value)) {
    return { __typedArray: value.constructor.name, length: (value as unknown as { length: number }).length };
  }
  if (Array.isArray(value)) {
    if (value.length > 32 && value.every((v) => typeof v === "number")) {
      return { __numericArray: true, length: value.length, preview: value.slice(0, 8) };
    }
    return value.slice(0, 32).map((v) => sanitize(v, maxStringLen));
  }
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = sanitize(v, maxStringLen);
  }
  return out;
}

const PING_INTERVAL_MS = 2500;
const PONG_TIMEOUT_MS = 180_000;
const BROADCAST_INTERVAL_MS = 1000;
const HISTORY_LIMIT = 100;

const PAGE_ID = `${location.pathname.replace(/^\//, "") || "root"}-${Math.random().toString(36).slice(2, 8)}`;

interface WorkerSlot {
  role: WorkerRole;
  id: string;
  worker: Worker;
  spawnedAt: number;
  lastMessageAt: number;
  heap: number | null;
  loaded: string[];
  toolsRun: number;
  currentProcId: string | null;
  currentToolId: string | null;
  currentInvocationId: string | null;
  lastStep: string | null;
  heartbeat: number | null;
  queue: string[];
}

interface ProcessRecord {
  id: string;
  invocationId: string;
  toolId: string;
  role: WorkerRole;
  workerId: string;
  label: string;
  status: "queued" | "running" | "ok" | "error";
  queuedAt: number;
  startedAt: number;
  endedAt: number | null;
  durationMs: number | null;
  steps: { at: number; label: string }[];
  result: unknown;
  error: string | null;
  outputBytes: number;
  resolve?: (v: unknown) => void;
  reject?: (e: Error) => void;
  pendingInput?: unknown;
  pendingTransfer?: Transferable[];
}

interface RunRecord {
  id: string;
  invocationId: string;
  agentId: string;
  fileName: string;
  status: "running" | "ok" | "error" | "cancelled";
  startedAt: number;
  endedAt: number | null;
  durationMs: number | null;
  procIds: string[];
  error: string | null;
  result?: unknown;
  resolve?: (v: unknown) => void;
  reject?: (e: Error) => void;
}

let nextRunSeq = 1;
let nextProcSeq = 1;

export interface AgentRunHandle {
  id: string;
  invocationId: string;
  promise: Promise<unknown>;
}

export class WorkerPoolSupervisor implements AgentSupervisor {
  readonly name: string;
  private workers = new Map<WorkerRole, WorkerSlot>();
  private runs = new Map<string, RunRecord>();
  private procs = new Map<string, ProcessRecord>();
  private history: string[] = [];
  private runHistory: string[] = [];
  private listeners = new Set<() => void>();
  private broadcaster: BroadcastChannel | null = null;
  private broadcastTimer: number;
  /** Overrides aplicados a inputs de tools. Set via /set-overrides ou --with. */
  private overrides: Record<string, Record<string, unknown>> = {};
  /** Throttle pra broadcast eager: pendente desde último flush. */
  private broadcastPending = false;
  private broadcastLastAt = 0;
  private static readonly BROADCAST_MIN_GAP_MS = 50;

  constructor(opts: { name?: string } = {}) {
    this.name = opts.name ?? "agentapp";
    try {
      this.broadcaster = new BroadcastChannel("wuwei-agent-pool");
      this.broadcaster.onmessage = (e) => {
        if (e.data?.type === "request-snapshot") this.broadcastNow();
      };
    } catch {
      this.broadcaster = null;
    }
    this.broadcastTimer = setInterval(
      () => this.broadcastNow(),
      BROADCAST_INTERVAL_MS,
    ) as unknown as number;
  }

  // === Pub/sub ===

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    try { fn(); } catch {}
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const fn of this.listeners) {
      try { fn(); } catch {}
    }
    // Broadcast eager (throttled) — garante que mudanças críticas (worker
    // spawn, proc start/done, run done) viram snapshot mesmo em tools <1s.
    this.scheduleBroadcast();
  }

  private scheduleBroadcast() {
    const now = Date.now();
    const gap = now - this.broadcastLastAt;
    if (gap >= WorkerPoolSupervisor.BROADCAST_MIN_GAP_MS) {
      // Já passou tempo suficiente — broadcast imediato.
      this.broadcastLastAt = now;
      this.broadcastNow();
      return;
    }
    if (this.broadcastPending) return;
    this.broadcastPending = true;
    setTimeout(() => {
      this.broadcastPending = false;
      this.broadcastLastAt = Date.now();
      this.broadcastNow();
    }, WorkerPoolSupervisor.BROADCAST_MIN_GAP_MS - gap);
  }

  private broadcastNow() {
    const msg = {
      type: "snapshot",
      pageId: PAGE_ID,
      pageTitle: document.title || location.pathname,
      ts: Date.now(),
      snapshot: this.snapshot(),
    };
    if (this.broadcaster) {
      try { this.broadcaster.postMessage(msg); } catch { /* */ }
    }
    // Publica também via relay HTTP (cross-process). Fail-silent.
    relayPublish(msg).catch(() => { /* */ });
  }

  // === Lifecycle ===

  private device: DeviceCapabilities = detectDevice();
  /** Marca último uso de cada worker pra LRU eviction. */
  private lastUsedAt = new Map<WorkerRole, number>();

  /** Cap de workers ativos baseado no tier. Mata o LRU idle pra liberar slot. */
  private enforceWorkerCap(): void {
    const cap = this.device.maxActiveWorkers;
    if (this.workers.size < cap) return;
    // Acha worker idle (sem currentProcId, sem queue) com último uso mais antigo.
    let victim: WorkerRole | null = null;
    let oldest = Infinity;
    for (const [role, slot] of this.workers) {
      if (slot.currentProcId || slot.queue.length > 0) continue;
      const lu = this.lastUsedAt.get(role) ?? slot.spawnedAt;
      if (lu < oldest) { oldest = lu; victim = role; }
    }
    if (victim) {
      console.log(`[pool] cap ${cap} atingido — matando worker idle '${victim}' (LRU)`);
      this.respawn(victim);
    }
    // Se todos estão ocupados: deixa estourar (a outra opção seria queue,
    // mas isso bloqueia agentes paralelos — preferimos overshoot temporário).
  }

  private ensureWorker(role: WorkerRole): WorkerSlot {
    const existing = this.workers.get(role);
    if (existing) {
      this.lastUsedAt.set(role, performance.now());
      return existing;
    }

    // Se ultrapassaria o cap do device tier, mata um idle primeiro
    this.enforceWorkerCap();

    const isExtractor = role === "extractor";
    const worker = isExtractor
      ? new ExtractorWorker()
      : new ToolWorker();
    // Boot message diz qual role o worker deve assumir.
    worker.postMessage({ type: "boot", role });

    const slot: WorkerSlot = {
      role,
      id: `${role}-w${Math.random().toString(36).slice(2, 6)}`,
      worker,
      spawnedAt: performance.now(),
      lastMessageAt: performance.now(),
      heap: null,
      loaded: [],
      toolsRun: 0,
      currentProcId: null,
      currentToolId: null,
      currentInvocationId: null,
      lastStep: null,
      heartbeat: null,
      queue: [],
    };

    worker.onmessage = (e) => this.handleMessage(slot, e.data);
    worker.onerror = (e) => {
      console.error(`[pool:${slot.id}] worker error`, e);
      this.failCurrent(slot, `worker error: ${e.message ?? "?"}`);
      this.respawn(role);
    };

    slot.heartbeat = setInterval(() => this.heartbeat(slot), PING_INTERVAL_MS) as unknown as number;
    this.workers.set(role, slot);
    this.emit();
    return slot;
  }

  private heartbeat(slot: WorkerSlot) {
    const since = performance.now() - slot.lastMessageAt;
    if (since > PONG_TIMEOUT_MS) {
      console.warn(`[pool:${slot.id}] hang ${(since / 1000).toFixed(0)}s — kill`);
      this.failCurrent(slot, `worker travado (${(since / 1000).toFixed(0)}s)`);
      this.respawn(slot.role);
      return;
    }
    try { slot.worker.postMessage({ type: "ping" }); } catch {}
  }

  private respawn(role: WorkerRole) {
    const slot = this.workers.get(role);
    if (!slot) return;
    if (slot.heartbeat != null) clearInterval(slot.heartbeat);
    try { slot.worker.terminate(); } catch {}
    this.workers.delete(role);
    this.emit();
  }

  killWorker(role: WorkerRole) {
    const slot = this.workers.get(role);
    if (slot) this.failCurrent(slot, "matado manualmente");
    this.respawn(role);
  }

  killAll() {
    for (const role of [...this.workers.keys()]) this.killWorker(role);
  }

  /** Acessa capabilities do device (tier, max workers, idle kill). */
  getDevice(): DeviceCapabilities { return this.device; }

  /** Agenda kill de worker idle se tier exigir cleanup. */
  private idleKillTimers = new Map<WorkerRole, number>();
  private scheduleIdleKill(role: WorkerRole): void {
    const ms = this.device.idleKillMs;
    if (ms <= 0) return; // tier high não mata nada
    const prev = this.idleKillTimers.get(role);
    if (prev != null) clearTimeout(prev);
    const tid = window.setTimeout(() => {
      const slot = this.workers.get(role);
      if (!slot || slot.currentProcId || slot.queue.length > 0) return;
      console.log(`[pool] idle ${ms}ms — matando worker '${role}' (libera RAM)`);
      this.respawn(role);
      this.idleKillTimers.delete(role);
    }, ms);
    this.idleKillTimers.set(role, tid);
  }

  /** Força um broadcast imediato (bypass do throttle). Útil pré-shutdown. */
  flush(): void {
    this.broadcastLastAt = Date.now();
    this.broadcastNow();
  }

  // === Overrides de parâmetros ===
  setOverrides(o: Record<string, Record<string, unknown>>) {
    this.overrides = o ?? {};
  }
  clearOverrides() {
    this.overrides = {};
  }
  getOverrides(): Record<string, Record<string, unknown>> {
    return this.overrides;
  }

  // === Mensagens do worker ===

  private handleMessage(slot: WorkerSlot, msg: { type: string;[k: string]: unknown }) {
    slot.lastMessageAt = performance.now();
    if (!msg || typeof msg !== "object") return;

    switch (msg.type) {
      case "pong":
        if (typeof msg.heap === "number") slot.heap = msg.heap;
        break;

      case "tool-step": {
        const proc = slot.currentProcId ? this.procs.get(slot.currentProcId) : null;
        if (proc) {
          const step = String(msg.step ?? "");
          proc.steps.push({ at: performance.now() - proc.startedAt, label: step });
          if (proc.steps.length > 200) proc.steps.splice(0, proc.steps.length - 200);
          slot.lastStep = step;
          relayPublish({
            type: "tool-step",
            invocationId: proc.invocationId,
            procId: proc.id,
            toolId: proc.toolId,
            step,
            ts: Date.now(),
          }).catch(() => { /* */ });
        }
        break;
      }

      case "tool-loaded":
        if (typeof msg.modelId === "string" && !slot.loaded.includes(msg.modelId)) {
          slot.loaded.push(msg.modelId);
        }
        break;

      case "tool-done": {
        const procId = String(msg.procId);
        const proc = this.procs.get(procId);
        if (proc) {
          proc.status = "ok";
          proc.endedAt = performance.now();
          proc.durationMs = (typeof msg.ms === "number" ? msg.ms : proc.endedAt - proc.startedAt);
          proc.result = msg.result;
          proc.outputBytes = jsonSize(msg.result);
          this.archiveProc(proc.id);
          slot.toolsRun++;
          slot.currentProcId = null;
          slot.currentToolId = null;
          slot.lastStep = `${proc.toolId} ✓ (${proc.durationMs} ms)`;
          this.lastUsedAt.set(slot.role, performance.now());
          this.scheduleIdleKill(slot.role);
          relayPublish({
            type: "tool-done",
            invocationId: proc.invocationId,
            procId: proc.id,
            toolId: proc.toolId,
            ms: proc.durationMs,
            result: sanitize(msg.result),
            ts: Date.now(),
          }).catch(() => { /* */ });
          proc.resolve?.(msg.result);
          this.dequeue(slot);
        }
        break;
      }

      case "tool-error": {
        const procId = String(msg.procId);
        const proc = this.procs.get(procId);
        if (proc) {
          proc.status = "error";
          proc.endedAt = performance.now();
          proc.durationMs = proc.endedAt - proc.startedAt;
          proc.error = String(msg.message ?? "erro desconhecido");
          this.archiveProc(proc.id);
          slot.currentProcId = null;
          slot.currentToolId = null;
          slot.lastStep = `${proc.toolId} ✗`;
          relayPublish({
            type: "tool-error",
            invocationId: proc.invocationId,
            procId: proc.id,
            toolId: proc.toolId,
            message: proc.error,
            durationMs: proc.durationMs,
            ts: Date.now(),
          }).catch(() => { /* */ });
          proc.reject?.(new Error(proc.error));
          this.dequeue(slot);
        }
        break;
      }
    }
    this.emit();
  }

  // === API: agent runs ===

  startRun(opts: { invocationId: string; agentId: string; fileName: string }): RunRecord {
    const id = `run${nextRunSeq++}`;
    const run: RunRecord = {
      id,
      invocationId: opts.invocationId,
      agentId: opts.agentId,
      fileName: opts.fileName,
      status: "running",
      startedAt: performance.now(),
      endedAt: null,
      durationMs: null,
      procIds: [],
      error: null,
    };
    this.runs.set(id, run);
    relayPublish({
      type: "invocation-start",
      invocationId: opts.invocationId,
      runId: id,
      agentId: opts.agentId,
      fileName: opts.fileName,
      ts: Date.now(),
    }).catch(() => { /* */ });
    this.emit();
    return run;
  }

  finishRun(invocationId: string, result: unknown) {
    for (const run of this.runs.values()) {
      if (run.invocationId === invocationId && run.status === "running") {
        run.status = "ok";
        run.endedAt = performance.now();
        run.durationMs = run.endedAt - run.startedAt;
        run.result = result;
        this.archiveRun(run.id);
        run.resolve?.(result);
        relayPublish({
          type: "invocation-done",
          invocationId,
          runId: run.id,
          agentId: run.agentId,
          status: "ok",
          durationMs: run.durationMs,
          ts: Date.now(),
        }).catch(() => { /* */ });
      }
    }
    this.emit();
  }

  failRun(invocationId: string, err: Error) {
    for (const run of this.runs.values()) {
      if (run.invocationId === invocationId && run.status === "running") {
        run.status = "error";
        run.endedAt = performance.now();
        run.durationMs = run.endedAt - run.startedAt;
        run.error = err.message;
        this.archiveRun(run.id);
        run.reject?.(err);
        relayPublish({
          type: "invocation-done",
          invocationId,
          runId: run.id,
          agentId: run.agentId,
          status: "error",
          durationMs: run.durationMs,
          error: err.message,
          ts: Date.now(),
        }).catch(() => { /* */ });
      }
    }
    this.emit();
  }

  // === API: tool dispatch ===

  log(invocationId: string, msg: string): void {
    // Adiciona como step "fake" ao último proc da invocação, se houver.
    // Não bloqueia se não houver — log de pré-execução é silencioso.
    for (const proc of this.procs.values()) {
      if (proc.invocationId === invocationId && proc.status === "running") {
        proc.steps.push({ at: performance.now() - proc.startedAt, label: `[log] ${msg}` });
        break;
      }
    }
    this.emit();
  }

  async runTool<I, O>(
    invocationId: string,
    toolId: string,
    input: I,
    opts: { transfer?: Transferable[]; label?: string } = {},
  ): Promise<O> {
    const role = TOOL_ROLES[toolId];
    if (!role) throw new Error(`tool desconhecida: ${toolId}`);
    const slot = this.ensureWorker(role);
    const procId = `proc${nextProcSeq++}`;
    // Aplica overrides (shallow merge — cobre {modelId, threshold, ...}).
    const ov = this.overrides[toolId];
    const finalInput = ov ? { ...(input as Record<string, unknown>), ...ov } : input;
    const proc: ProcessRecord = {
      id: procId,
      invocationId,
      toolId,
      role,
      workerId: slot.id,
      label: opts.label ?? toolId,
      status: "queued",
      queuedAt: performance.now(),
      startedAt: performance.now(),
      endedAt: null,
      durationMs: null,
      steps: [],
      result: null,
      error: null,
      outputBytes: 0,
      pendingInput: finalInput,
      pendingTransfer: opts.transfer,
    };
    this.procs.set(procId, proc);
    const run = [...this.runs.values()].find(
      (r) => r.invocationId === invocationId && r.status === "running",
    );
    if (run) run.procIds.push(procId);

    return new Promise<O>((resolve, reject) => {
      proc.resolve = resolve as (v: unknown) => void;
      proc.reject = reject;
      if (slot.currentProcId) {
        slot.queue.push(procId);
      } else {
        this.dispatch(slot, proc);
      }
      this.emit();
    });
  }

  private dequeue(slot: WorkerSlot) {
    while (slot.queue.length > 0 && !slot.currentProcId) {
      const next = slot.queue.shift()!;
      const proc = this.procs.get(next);
      if (proc) this.dispatch(slot, proc);
    }
  }

  private dispatch(slot: WorkerSlot, proc: ProcessRecord) {
    slot.currentProcId = proc.id;
    slot.currentToolId = proc.toolId;
    slot.currentInvocationId = proc.invocationId;
    proc.status = "running";
    proc.startedAt = performance.now();
    relayPublish({
      type: "tool-start",
      invocationId: proc.invocationId,
      procId: proc.id,
      toolId: proc.toolId,
      role: proc.role,
      workerId: slot.id,
      input: sanitize(proc.pendingInput),
      ts: Date.now(),
    }).catch(() => { /* */ });
    try {
      const msg = {
        type: "tool-run",
        procId: proc.id,
        toolId: proc.toolId,
        input: proc.pendingInput,
      };
      if (proc.pendingTransfer && proc.pendingTransfer.length > 0) {
        slot.worker.postMessage(msg, proc.pendingTransfer);
      } else {
        slot.worker.postMessage(msg);
      }
    } catch (e) {
      proc.status = "error";
      proc.endedAt = performance.now();
      proc.error = `postMessage falhou: ${(e as Error).message}`;
      this.archiveProc(proc.id);
      slot.currentProcId = null;
      proc.reject?.(e as Error);
    }
    this.emit();
  }

  private failCurrent(slot: WorkerSlot, reason: string) {
    if (!slot.currentProcId) return;
    const proc = this.procs.get(slot.currentProcId);
    if (!proc || proc.status === "ok" || proc.status === "error") return;
    proc.status = "error";
    proc.endedAt = performance.now();
    proc.durationMs = proc.endedAt - proc.startedAt;
    proc.error = reason;
    this.archiveProc(proc.id);
    proc.reject?.(new Error(reason));
    slot.currentProcId = null;
    slot.currentToolId = null;
  }

  private archiveProc(id: string) {
    this.history.unshift(id);
    while (this.history.length > HISTORY_LIMIT) this.history.pop();
  }

  private archiveRun(id: string) {
    this.runHistory.unshift(id);
    while (this.runHistory.length > 50) this.runHistory.pop();
  }

  // === Snapshot ===

  snapshot() {
    const now = performance.now();
    const mainHeap = (() => {
      try {
        const m = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
        return m ? m.usedJSHeapSize : null;
      } catch { return null; }
    })();

    const workers = [...this.workers.values()].map((slot) => ({
      role: slot.role,
      id: slot.id,
      uptimeMs: now - slot.spawnedAt,
      silentForMs: now - slot.lastMessageAt,
      heap: slot.heap ?? mainHeap,
      heapSource: slot.heap != null ? "worker" : (mainHeap != null ? "main" : null),
      currentProcId: slot.currentProcId,
      currentToolId: slot.currentToolId,
      lastStep: slot.lastStep,
      toolsRun: slot.toolsRun,
      loaded: [...slot.loaded],
    }));

    const seen = new Set<string>();
    const procs: unknown[] = [];
    for (const slot of this.workers.values()) {
      if (slot.currentProcId) {
        const p = this.procs.get(slot.currentProcId);
        if (p) {
          procs.push(this.viewProc(p, slot, now));
          seen.add(p.id);
        }
      }
    }
    for (const id of this.history.slice(0, 30)) {
      if (seen.has(id)) continue;
      const p = this.procs.get(id);
      if (p) procs.push(this.viewProc(p, this.slotOfWorker(p.workerId), now));
    }

    const runs: unknown[] = [];
    for (const r of this.runs.values()) {
      if (r.status === "running") runs.push(this.viewRun(r, now));
    }
    for (const id of this.runHistory.slice(0, 10)) {
      const r = this.runs.get(id);
      if (r && r.status !== "running") runs.push(this.viewRun(r, now));
    }

    return { name: this.name, pageId: PAGE_ID, workers, procs, runs };
  }

  private viewProc(p: ProcessRecord, slot: WorkerSlot | null, now: number) {
    return {
      id: p.id,
      invocationId: p.invocationId,
      toolId: p.toolId,
      role: p.role,
      workerId: p.workerId,
      label: p.label,
      status: p.status,
      startedAt: p.startedAt,
      endedAt: p.endedAt,
      sinceStartMs: (p.endedAt ?? now) - p.startedAt,
      sinceLastMsgMs: slot && p.status === "running" ? now - slot.lastMessageAt : null,
      lastStep: p.steps[p.steps.length - 1]?.label ??
        (p.status === "ok" ? "ok" : p.status === "error" ? p.error : "—"),
      stepCount: p.steps.length,
      durationMs: p.durationMs,
      heapSnapshot: slot ? slot.heap : null,
      error: p.error,
      outputBytes: p.outputBytes,
    };
  }

  private viewRun(r: RunRecord, now: number) {
    return {
      id: r.id,
      invocationId: r.invocationId,
      agentId: r.agentId,
      fileName: r.fileName,
      status: r.status,
      startedAt: r.startedAt,
      endedAt: r.endedAt,
      durationMs: r.durationMs ?? (now - r.startedAt),
      procCount: r.procIds.length,
      error: r.error,
    };
  }

  private slotOfWorker(workerId: string): WorkerSlot | null {
    for (const slot of this.workers.values()) {
      if (slot.id === workerId) return slot;
    }
    return null;
  }
}

function jsonSize(obj: unknown): number {
  try {
    return JSON.stringify(obj ?? null).length;
  } catch {
    return 0;
  }
}
